import { useEffect, useRef } from 'react';
import simulationService from '@/services/simulationService';
import type { SimulationDocumentResponse } from '@/types/simulation';

const POLL_INTERVAL_MS = 8000;

const INTERMEDIATE: ReadonlySet<SimulationDocumentResponse['processingStatus']> = new Set([
  'Pending',
  'Extracting',
  'Summarizing',
]);

/**
 * Configurator-specific poller: refreshes the full document list of a single
 * simulation every POLL_INTERVAL_MS while any doc is in an intermediate
 * processing state. When all docs reach a terminal state (Ready/Failed/Rejected),
 * the interval tears down and no further requests are issued.
 *
 * The hook calls onUpdate with the full refreshed list so the form state
 * stays the single source of truth.
 */
export function useSimulationDocumentsPoller(
  simulationId: string | null,
  documents: SimulationDocumentResponse[] | undefined,
  onUpdate: (docs: SimulationDocumentResponse[]) => void,
): void {
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const hasIntermediate =
    (documents ?? []).some((d) => INTERMEDIATE.has(d.processingStatus));

  useEffect(() => {
    if (!simulationId || !hasIntermediate) return;

    let cancelled = false;

    const tick = async () => {
      try {
        const fresh = await simulationService.getDocuments(simulationId);
        if (cancelled) return;
        onUpdateRef.current(fresh);
      } catch {
        // Transient failures do not kill the loop.
      }
    };

    const handle = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [simulationId, hasIntermediate]);
}
